import { Link } from 'react-router-dom'
import { Eye, UserCheck, Briefcase, Users2, ArrowLeft, Shield, Lock, Mail, Check, X, AlertTriangle, Info } from 'lucide-react'
import { Button, Card, Badge, PageHeader } from '../components/ui/SharedUI'

const SHARE_GUIDE = [
  {
    id: 'verification',
    icon: UserCheck,
    label: 'Shift verification',
    color: '#2C7A7B',
    bg: '#E1F5EE',
    tagline: 'For confirming individual shifts happened as documented.',
    bestFor: [
      'A family member who was present during shifts and can confirm they happened',
      'A care recipient proxy who can validate visits',
      'A neighbor or trusted contact who witnessed care being delivered',
    ],
    canSee: [
      'The shift date, times, and duration',
      'Shift type (direct care, no-show covered, etc.)',
      'Tasks performed during the shift',
      'Whether the shift was locked (tamper-evident)',
    ],
    canDo: [
      'Confirm a shift happened as documented',
      'Dispute a shift with an optional written explanation',
      'Mark a shift as "unsure" if they cannot verify',
    ],
    cannotSee: [
      'Evidence notes',
      'Pay rates or earnings',
      'Other recipients\' shifts',
    ],
    requires: [
      { label: 'Full name', required: true },
      { label: 'Email address', required: true, note: 'Required for identity verification' },
      { label: 'Care recipient', required: false, note: 'Leave blank to share all recipients' },
    ],
    example: {
      title: 'Example: Your sister confirming Mom\'s care',
      text: 'You care for your mom on weekdays. Your sister stops by weekly and can vouch for whether shifts happened. You create a verification share with her name and email. She opens the link, verifies her email, and marks each shift as confirmed, disputed, or unsure. Her responses become permanent corroboration records tied to her verified email.',
    },
    security: 'Interactive share — email verification required before the person can act. Responses are tamper-evident and cannot be edited or deleted after submission.',
  },
  {
    id: 'oversight',
    icon: Eye,
    label: 'Care oversight',
    color: '#005440',
    bg: '#E1F5EE',
    tagline: 'For family members managing a loved one\'s overall care.',
    bestFor: [
      'An adult child overseeing care for an aging parent',
      'A spouse or partner monitoring care their loved one receives',
      'A family member coordinating with other caregivers',
      'A power of attorney or healthcare proxy',
    ],
    canSee: [
      'All shifts logged for the recipient',
      'Evidence notes (timestamped documentation)',
      'Shift types, durations, and tasks',
      'Corroboration history from other family members',
    ],
    canDo: [
      'View comprehensive care records',
      'Print or screenshot for their own reference',
    ],
    cannotSee: [
      'Pay rates or earnings',
      'Other recipients\' shifts (unless shared)',
    ],
    requires: [
      { label: 'Full name', required: true },
      { label: 'Email address', required: false, note: 'Optional — no verification needed' },
      { label: 'Relationship to care recipient', required: false, note: 'e.g. Sister, Son, Grandchild' },
      { label: 'Care recipient', required: false },
    ],
    example: {
      title: 'Example: Your brother watching over Mom\'s care',
      text: 'Your brother lives out of state but wants to stay informed about Mom\'s daily care. You create an oversight share so he can see all the shifts you and other aides log, along with evidence notes about incidents or observations. He can\'t confirm or dispute anything, but he has a complete window into how she\'s doing.',
    },
    security: 'Read-only share — no email verification needed since no interaction is possible. The person can view but cannot modify anything.',
  },
  {
    id: 'professional',
    icon: Briefcase,
    label: 'Professional review',
    color: '#8b5000',
    bg: '#FCF5E8',
    tagline: 'For case managers, attorneys, and investigators reviewing your records.',
    bestFor: [
      'A case manager reviewing care delivery for a Medicaid waiver client',
      'An attorney building a wage dispute or benefits case',
      'A legal aid organization supporting your claim',
      'A Department of Labor investigator reviewing documentation',
      'A social worker coordinating comprehensive care planning',
    ],
    canSee: [
      'All shifts with complete detail',
      'Evidence notes and timestamps',
      'Pay rates and documented earnings',
      'Full corroboration history',
      'Agency allocation data (if configured)',
    ],
    canDo: [
      'Export shift records as CSV for case files',
      'Print or download records for their case management system',
      'Reference complete data during meetings or hearings',
    ],
    cannotSee: [
      'Other recipients they were not granted access to',
    ],
    requires: [
      { label: 'Full name', required: true },
      { label: 'Email address', required: true, note: 'Required — verification enforced before export' },
      { label: 'Organization', required: false, note: 'e.g. 360 Cares, Georgia Legal Services' },
      { label: 'Role or title', required: false, note: 'e.g. Case Manager, Attorney' },
      { label: 'Care recipient', required: false },
    ],
    example: {
      title: 'Example: Your case manager reviewing care records',
      text: 'Annette Barnes at 360 Cares is Mom\'s case manager. During her monthly check-in, she needs to review care delivery. You create a professional share with her name, email, organization, and title. She verifies her email, reviews the full record including pay data, and exports the CSV to attach to her case management system. Her access is tied to her verified professional identity.',
    },
    security: 'Highest-privilege share — email verification required. Export capability enabled. All access is logged with timestamps. Consider setting a shorter expiration (30-90 days) for temporary case reviews.',
  },
  {
    id: 'coordination',
    icon: Users2,
    label: 'Care team coordination',
    color: '#4A5568',
    bg: '#F7FAFC',
    tagline: 'For other caregivers coordinating schedule and coverage.',
    bestFor: [
      'Another paid aide who covers the same client on different days',
      'A family member who also provides direct care',
      'A relief caregiver arranging coverage',
      'A rotating team of caregivers managing shift handoffs',
    ],
    canSee: [
      'When shifts were scheduled and worked (times only)',
      'Basic shift types (direct care, training, etc.)',
      'Coverage gaps in the schedule',
    ],
    canDo: [
      'Coordinate their own schedule around existing shifts',
      'Identify gaps that need coverage',
      'See who has been assigned when',
    ],
    cannotSee: [
      'Evidence notes',
      'Incident flags',
      'Task details',
      'Pay rates or earnings',
      'Corroboration history',
    ],
    requires: [
      { label: 'Full name', required: true },
      { label: 'Email address', required: false, note: 'Optional — no verification needed' },
      { label: 'Care recipient', required: false },
    ],
    example: {
      title: 'Example: Coordinating with a co-aide',
      text: 'You and another aide, Suzanne, both work with the same client. She needs to know your schedule to plan her own shifts, but doesn\'t need to see your evidence notes or pay data. You create a coordination share for her — she sees your shift times and can plan around them without accessing any sensitive care details.',
    },
    security: 'Minimal-privilege share — schedule-only view protects sensitive care documentation. No interaction possible.',
  },
]

export function SharingGuidePage() {
  return (
    <div style={{ padding: '2rem', maxWidth: '900px' }}>
      <div style={{ marginBottom: '1rem' }}>
        <Link to="/sharing" style={{ color: '#2C7A7B', fontSize: '14px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <ArrowLeft size={14} /> Back to sharing
        </Link>
      </div>

      <PageHeader
        title="Sharing guide"
        description="Choose the right share type for who you're sharing with. Every share type has different visibility, actions, and security controls."
      />

      {/* Philosophy section */}
      <Card style={{ marginBottom: '2rem', background: '#F7FAFC', borderLeft: '3px solid #2C7A7B' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
          <Shield size={20} style={{ color: '#005440', marginTop: '2px', flexShrink: 0 }} />
          <div style={{ fontSize: '14px', color: '#4A5568', lineHeight: 1.7 }}>
            <strong style={{ color: '#1A202C' }}>How sharing works in KindredCare.</strong> You own your care records. Sharing means granting scoped, revocable access to specific people — not giving away control. Every share can be revoked instantly. Every action taken through a share is timestamped and logged. Different share types give different levels of access so you can share the minimum needed for each person's role.
          </div>
        </div>
      </Card>

      {/* Detailed breakdown for each share type */}
      {SHARE_GUIDE.map(type => {
        const Icon = type.icon
        return (
          <div key={type.id} id={type.id} style={{ marginBottom: '2rem' }}>
            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px',
              paddingBottom: '10px', borderBottom: `2px solid ${type.color}`,
            }}>
              <div style={{ padding: '10px', background: type.bg, borderRadius: '10px' }}>
                <Icon size={22} style={{ color: type.color }} />
              </div>
              <div>
                <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '22px', fontWeight: 500, color: '#1A202C', marginBottom: '2px' }}>
                  {type.label}
                </h2>
                <div style={{ fontSize: '14px', color: '#4A5568' }}>{type.tagline}</div>
              </div>
            </div>

            {/* Body */}
            <Card>
              {/* Best for */}
              <div style={{ marginBottom: '1.25rem' }}>
                <h3 style={sectionTitleStyle}>Best for</h3>
                <ul style={listStyle}>
                  {type.bestFor.map((item, i) => (
                    <li key={i} style={listItemStyle}>{item}</li>
                  ))}
                </ul>
              </div>

              {/* What they can see and do */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '1.25rem' }}>
                <div>
                  <h3 style={sectionTitleStyle}><Check size={14} style={{ color: '#0F6E56', verticalAlign: 'middle', marginRight: '4px' }} /> They can see</h3>
                  <ul style={listStyle}>
                    {type.canSee.map((item, i) => (
                      <li key={i} style={listItemStyle}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 style={sectionTitleStyle}><X size={14} style={{ color: '#993C1D', verticalAlign: 'middle', marginRight: '4px' }} /> They cannot see</h3>
                  <ul style={listStyle}>
                    {type.cannotSee.map((item, i) => (
                      <li key={i} style={listItemStyle}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Can do */}
              <div style={{ marginBottom: '1.25rem' }}>
                <h3 style={sectionTitleStyle}>What they can do</h3>
                <ul style={listStyle}>
                  {type.canDo.map((item, i) => (
                    <li key={i} style={listItemStyle}>{item}</li>
                  ))}
                </ul>
              </div>

              {/* Required information */}
              <div style={{ marginBottom: '1.25rem' }}>
                <h3 style={sectionTitleStyle}>Information you'll be asked for</h3>
                <div style={{ display: 'grid', gap: '6px' }}>
                  {type.requires.map((r, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'flex-start', gap: '8px',
                      padding: '8px 12px', background: '#F7FAFC', borderRadius: '6px', fontSize: '13px',
                    }}>
                      <span style={{ fontWeight: 500, color: '#1A202C', minWidth: '160px' }}>{r.label}</span>
                      <span style={{ color: r.required ? '#993C1D' : '#718096', fontSize: '12px', fontWeight: 500 }}>
                        {r.required ? 'Required' : 'Optional'}
                      </span>
                      {r.note && <span style={{ color: '#718096', flex: 1 }}>· {r.note}</span>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Example */}
              <div style={{ marginBottom: '1.25rem', padding: '14px 16px', background: type.bg, borderRadius: '8px' }}>
                <h3 style={{ ...sectionTitleStyle, color: type.color, marginBottom: '6px' }}>{type.example.title}</h3>
                <p style={{ fontSize: '13px', color: '#4A5568', lineHeight: 1.7, margin: 0 }}>
                  {type.example.text}
                </p>
              </div>

              {/* Security note */}
              <div style={{
                padding: '12px 14px', background: '#FCF5E8', borderRadius: '8px',
                display: 'flex', gap: '10px', alignItems: 'flex-start',
              }}>
                <Lock size={16} style={{ color: '#8b5000', marginTop: '2px', flexShrink: 0 }} />
                <div style={{ fontSize: '13px', color: '#8b5000', lineHeight: 1.6 }}>
                  <strong>Security:</strong> {type.security}
                </div>
              </div>
            </Card>
          </div>
        )
      })}

      {/* How security works */}
      <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '20px', fontWeight: 500, color: '#1A202C', marginTop: '3rem', marginBottom: '1rem' }}>
        How access controls work
      </h2>

      <Card style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'grid', gap: '16px' }}>
          <SecurityRow
            icon={Mail}
            title="Email verification for interactive shares"
            body="For shift verification and professional review shares, the person opening the link must verify their email before they can confirm, dispute, or export. This ties their actions to a real identity that you control."
          />
          <SecurityRow
            icon={Lock}
            title="Tamper-evident by design"
            body="Every shift locks 30 minutes after clock-out. Every corroboration is append-only — once submitted, it cannot be edited or deleted. This is what makes the records evidentiary."
          />
          <SecurityRow
            icon={AlertTriangle}
            title="Disputes flag but never delete"
            body="If someone disputes a shift, the shift stays on your record. The dispute is recorded permanently alongside your original entry. Both remain visible. No one can silently erase your work."
          />
          <SecurityRow
            icon={Shield}
            title="Revoke access at any time"
            body="If you no longer want someone to have access, revoke the share instantly. They can no longer open the link. Any corroborations they already submitted remain on your record."
          />
        </div>
      </Card>

      {/* FAQ */}
      <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '20px', fontWeight: 500, color: '#1A202C', marginTop: '2rem', marginBottom: '1rem' }}>
        Common questions
      </h2>

      <div style={{ display: 'grid', gap: '10px' }}>
        <FAQ
          q="What if I set the wrong email for someone?"
          a="Revoke the share and create a new one with the correct email. The person will need the new link, and any verification they completed on the old share doesn't carry over."
        />
        <FAQ
          q="Can someone confirm a shift I logged incorrectly?"
          a="Yes, and that's the point. If a shift is confirmed by someone else, that adds evidentiary weight to your record. If they dispute it, the dispute becomes part of the record alongside your original entry."
        />
        <FAQ
          q="Are auto-notification emails sent when I create a share?"
          a="Not yet. Right now you copy the link and send it yourself via text, email, or however you normally reach the person. Automatic notification emails are on the roadmap for Phase 2."
        />
        <FAQ
          q="What happens when a share expires?"
          a="The link stops working. The person can no longer open the shared view. Any corroborations they already submitted stay on your record. You can create a new share if needed."
        />
        <FAQ
          q="Can I share with multiple people at once?"
          a="Not yet — each share is one-to-one. This is deliberate for now: it means every action taken through a share is tied to a specific identified person. Group shares may come in a future release."
        />
      </div>

      <div style={{ marginTop: '2rem', textAlign: 'center' }}>
        <Link to="/sharing" style={{ textDecoration: 'none' }}>
          <Button>← Back to sharing</Button>
        </Link>
      </div>
    </div>
  )
}

function SecurityRow({ icon: Icon, title, body }) {
  return (
    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
      <div style={{ padding: '8px', background: '#E1F5EE', borderRadius: '8px', flexShrink: 0 }}>
        <Icon size={16} style={{ color: '#005440' }} />
      </div>
      <div>
        <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A202C', marginBottom: '2px' }}>{title}</div>
        <div style={{ fontSize: '13px', color: '#4A5568', lineHeight: 1.6 }}>{body}</div>
      </div>
    </div>
  )
}

function FAQ({ q, a }) {
  return (
    <Card>
      <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A202C', marginBottom: '4px' }}>{q}</div>
      <div style={{ fontSize: '13px', color: '#4A5568', lineHeight: 1.6 }}>{a}</div>
    </Card>
  )
}

const sectionTitleStyle = {
  fontSize: '12px',
  fontWeight: 600,
  color: '#4A5568',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '8px',
}

const listStyle = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'grid',
  gap: '4px',
}

const listItemStyle = {
  fontSize: '13px',
  color: '#4A5568',
  lineHeight: 1.6,
  paddingLeft: '14px',
  position: 'relative',
}
